import { useState } from "react";
import { useApp } from "../context/AppContext";
import ModelModal from "../components/ModelModal";
import CreateCustomModelModal from "../components/CreateCustomModelModal";

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function ModelsPage() {
  const { models: rawModels, loadingModels, showToast, setModels, fetchModels, trackEvent } = useApp();

  const [modelSearch, setModelSearch] = useState("");
  const [activeProviderTab, setActiveProviderTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);

  // Process models to create a "Custom" group at the top
  const models = (() => {
    if (!rawModels) return null;

    const customModels = [];
    const providerGroups = rawModels.map(group => {
      // Separate custom models from this group
      const rawGroupModels = group.models || [];
      const standard = rawGroupModels.filter(m => !m.isCustom);
      const custom = rawGroupModels.filter(m => m.isCustom);
      customModels.push(...custom);
      return { ...group, models: standard };
    }).filter(g => g.models.length > 0); // Remove empty groups

    // If we have custom models, put them first in a specific group
    if (customModels.length > 0) {
      return [{ provider: "custom", models: customModels }, ...providerGroups];
    }

    return providerGroups;
  })();
  // Custom Model State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  function isNewModel(m) {
    const v = m?.globalCreatedAt;
    if (!v) return false;
    const created = new Date(v);
    if (Number.isNaN(created.getTime())) return false;
    const days = 14; //it is decided to be 14, but keep short initially to avoid "everything is new" after rollout
    return (Date.now() - created.getTime()) <= days * 24 * 60 * 60 * 1000;
  }

  function matchesQuery(pm, m, q) {
    const term = (q || "").trim().toLowerCase();
    if (!term) return true;
    const name = (m.name || "").toLowerCase();
    const display = (m.displayName || "").toLowerCase();
    const provider = (pm.provider || "").toLowerCase();
    return name.includes(term) || display.includes(term) || provider.includes(term);
  }

  function toggleModel(provider, model, enabled) {
    // Resolve the model object if a string name was passed
    let item = model;
    if (typeof model === "string") {
      const pm = models.find(x => x.provider === provider);
      item = pm?.models?.find(x => x.name === model);
    }


    if (!item) return;

    // Optimistic Update
    setModels(rawModels.map(group => {
      // Check if the model exists in this group (ignoring provider string mismatch for custom models)
      const hasModel = group.models.some(m => m.name === item.name);

      if (hasModel) {
        return {
          ...group,
          models: group.models.map(m =>
            m.name === item.name ? { ...m, enabled } : m
          )
        };
      }
      return group;
    }));

    setLoading(true);
    fetch(`${API_BASE}/api/models/${item.id}/toggle`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    })
      .then(r => {
        if (!r.ok) throw new Error("Failed to toggle model");
        showToast(`Model ${enabled ? "enabled" : "disabled"}`, "success");
        trackEvent("model_toggle", { provider, model, enabled: !!enabled });
      })
      .catch(err => {
        showToast(err.message, "error");
        fetchModels();
      })
      .finally(() => setLoading(false));
  }

  return (
    <>
      <div className="card">
        <div className="card-header">
          <svg className="card-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
          </svg>
          <h2>Available Models</h2>
        </div>
        {loadingModels || models === null ? (
          <div className="spinner"></div>
        ) : models.length === 0 ? (
          <div className="empty-state">
            Add an API key to see available models
          </div>
        ) : (
          <>
            <div className="model-filters">
              <div className="search-box">
                <svg className="search-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search models..."
                  className="search-input"
                  value={modelSearch}
                  onChange={e => setModelSearch(e.target.value)}
                />
                {modelSearch && (
                  <button className="search-clear" onClick={() => setModelSearch("")}>
                    ✕
                  </button>
                )}
              </div>
              <div style={{
                display: "flex",
                gap: "1rem",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "space-between",
                marginBottom: "1rem"
              }}>
                <div className="provider-tabs">
                  <button
                    className={`tab ${activeProviderTab === "all" ? "tab-active" : ""}`}
                    onClick={() => setActiveProviderTab("all")}
                  >
                    All
                  </button>
                  {models.map(pm => (
                    <button
                      key={pm.provider}
                      className={`tab ${activeProviderTab === pm.provider ? "tab-active" : ""}`}
                      onClick={() => setActiveProviderTab(pm.provider)}
                    >
                      {pm.provider === "custom"
                        ? "CUSTOM"
                        : (pm.provider ? pm.provider.toUpperCase() : "??")
                      }
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                    disabled={loading || activeProviderTab === "all"}
                    onClick={async () => {
                      setLoading(true);
                      trackEvent("models_manual_sync", { provider: activeProviderTab });
                      try {
                        const res = await fetch(`${API_BASE}/api/models/sync`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ provider: activeProviderTab }),
                        });
                        if (!res.ok) throw new Error("Sync failed");
                        await fetchModels();
                        showToast("Models synced", "success");
                      } catch (err) {
                        showToast(err.message, "error");
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24" style={{ animation: loading ? "spin 1s linear infinite" : "none" }}>
                      <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
                    </svg>
                    Sync
                  </button>
                  <button
                    className="btn btn-primary"
                    style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                    onClick={() => setIsCreateModalOpen(true)}
                  >
                    <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                    Create Model
                  </button>
                </div>
              </div>
            </div>
            {models
              .filter(pm =>
                activeProviderTab === "all" || pm.provider === activeProviderTab
              )
              .map((pm, i, arr) => {
                const filteredModels =
                  pm.models?.filter(m => matchesQuery(pm, m, modelSearch)) || [];

                if (filteredModels.length === 0) return null;

                return (
                  <div key={pm.provider}>
                    {activeProviderTab === "all" && (
                      <div className="badge" style={{ marginBottom: "1rem" }}>
                        {pm.provider === "custom"
                          ? "CUSTOM MODELS"
                          : (pm.provider ? pm.provider.toUpperCase() : "??")}
                      </div>
                    )}
                    <div className="model-grid">
                      {filteredModels.map((m, j) => (
                        <div
                          key={m.id || j}
                          className="model-item model-toggle-row"
                          onClick={() => setSelectedModel({ provider: pm.provider, ...m })}
                        >
                          {isNewModel(m) && (
                            <span className="model-new-badge">NEW</span>
                          )}
                          {m.isCustom && (
                            <span className="custom-model-badge">CUSTOM</span>
                          )}
                          <div className="model-names">
                            <div className="model-display-name">{m.displayName}</div>
                            <div className="model-id">{m.isCustom && m.baseModelId ? m.baseModelId : m.name}</div>
                          </div>

                          {/* Just block propagation on the container, let the Input change handle logic */}
                          <div
                            className="switch"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleModel(pm.provider, m, !m.enabled);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!m.enabled}
                              readOnly
                              disabled={loading}
                            />
                            <span className="slider"></span>
                          </div>
                          <div className="model-meta-left" onClick={(e) => e.stopPropagation()}>

                            {!m.isCustom && m.costPer1kCredits > 0 && (
                              <div className="model-price-tag">
                                ~{m.costPer1kCredits} cr/k
                                <div className="model-price-info" tabIndex="0" onClick={(e) => e.stopPropagation()}>
                                  <svg className="model-price-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path d="M12 16v-4"></path>
                                    <path d="M12 8h.01"></path>
                                  </svg>
                                  <div className="model-price-tooltip">
                                    <div className="model-price-tooltip-header">
                                      ~{m.costPer1kCredits} credits per 1k tokens
                                    </div>
                                    Estimated cost based on a standard conversation mix (reading vs. writing). Actual usage varies as generating text costs more than reading context. Minimum charge: 1 credit per request.
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                      ))}
                    </div>
                    {activeProviderTab === "all" && i < arr.length - 1 && <div className="divider"></div>}
                  </div>
                );
              })}
            {models.every(pm =>
              !pm.models?.some(m => matchesQuery(pm, m, modelSearch))
            ) && modelSearch && (
                <div className="empty-state">
                  No models found matching "{modelSearch}"
                </div>
              )}
          </>
        )}
      </div >

      {selectedModel && (
        <ModelModal
          model={selectedModel}
          allModels={models} // Pass all models so we can populate the dropdown
          onClose={() => setSelectedModel(null)}
          onSuccess={() => { setSelectedModel(null); fetchModels(); }}
        />
      )
      }
      {
        isCreateModalOpen && (
          <CreateCustomModelModal
            models={rawModels} // Pass raw models to dropdowns to avoid showing "Custom" as a provider option
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={() => { setIsCreateModalOpen(false); fetchModels(); }}
          />
        )
      }
    </>
  );
}
